import React from 'react';
import { useSelector } from 'react-redux';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { getBasePath } from 'js/utils/general';

// import icons
import IconCardBackPattern from 'js/components/icon/Icon.card-back-pattern';
import IconCornerPattern from 'js/components/icon/Icon.corner-pattern';
import IconCardBackCharPattern from 'js/components/icon/Icon.card-back-char-pattern';
import IconZigZagTitleUnderline from 'js/components/icon/Icon.zig-zag-title-underline';

// import components
import SwiperCardBack from 'js/components/swiper/Swiper.card-back';

// import selectors
import { getTranslationById } from 'js/store/app/App.selectors';

const normalizeText = (text) => {
  if (!text) return '';
  return (
    text
      ?.toLowerCase()
      ?.replace(/[^a-z0-9]+/g, '-')
      ?.replace(/^-+|-+$/g, '') || text
  );
};

const ActivityCardBack = ({ data, swiperRef }) => {
  const {
    description = '',
    youWillNeed = 'Nothing',
    bigIdea = '',
    howToPlay1 = '',
    title = '',
    sesameCharacter = '',
    gameplayTip = '',
    gameplayVisual = '',
    localisation = {},
  } = data?.back || {};

  const {
    title: localizedTitle,
    description: localizedDescription,
    youWillNeed: localizedYouWillNeed,
    howToPlay1: localizedHowToPlay1,
    bigIdea: localizedBigIdea,
    gameplayTip: localizedGameplayTip,
  } = localisation || {};

  const locale = useSelector((state) => state.app.locale);
  const siteText = {
    materials: useSelector((state) => getTranslationById(state, 'card_back_materials', 'Materials:')),
    bigIdea: useSelector((state) => getTranslationById(state, 'card_back_big_idea', 'Big Idea:')),
    howToPlay: useSelector((state) => getTranslationById(state, 'card_back_how_to_play', 'How to Play')),
    gameplayTip: useSelector((state) => getTranslationById(state, 'card_back_gameplay_tip', 'Gameplay Tip')),
  };

  const cardTitle = locale === 'es' ? localizedTitle : title;
  const cardDescription = locale === 'es' ? localizedDescription : description;
  const cardYouWillNeed = locale === 'es' ? localizedYouWillNeed : youWillNeed;
  const cardBigIdea = locale === 'es' ? localizedBigIdea : bigIdea;
  const cardHowToPlay1 = locale === 'es' ? localizedHowToPlay1 : howToPlay1;
  const cardGameplayTip = locale === 'es' ? localizedGameplayTip : gameplayTip;

  const characterClassName = normalizeText(sesameCharacter);
  const characterBackImage = `${
    getBasePath() === '/' ? getBasePath() : getBasePath() + '/'
  }images/static/card_back_${characterClassName}.png`;

  const characterBackFullImage = `${
    getBasePath() === '/' ? getBasePath() : getBasePath() + '/'
  }images/static/card_back_full_${characterClassName}.png`;

  const renderSlides = () => {
    const slides = [];

    if (cardDescription && cardDescription !== '') {
      slides.push(
        <div key="back_text_1" className="card__activity-details">
          <div className="generic-background-pattern">
            <IconCardBackPattern />
          </div>
          <OverlayScrollbarsComponent
            className="custom-scrollbar card__scrollbar-description"
            options={{ overflow: { x: 'hidden' } }}
          >
            <div className="card__activity-details-content">
              <h3 className="card__back-title">{cardTitle}</h3>
              <p className="card__back-description">{cardDescription}</p>
              <div className="card__prep-need">
                <p className="card__prep-need-content">
                  <strong>{siteText.materials}</strong> {cardYouWillNeed == '' ? 'Nothing!' : cardYouWillNeed}
                </p>
                <p className="card__prep-need-content">
                  <strong>{siteText.bigIdea}</strong> {cardBigIdea}
                </p>
              </div>
            </div>
          </OverlayScrollbarsComponent>
          <div className="card__back-character-footer">
            <IconCardBackCharPattern />
            <img
              className="card__back-character-image"
              src={characterBackImage}
              alt={`${sesameCharacter} character image`}
            />
          </div>
        </div>
      );
    }

    if (cardHowToPlay1 && cardHowToPlay1 !== '') {
      slides.push(
        <div key="back_text_2" className="card__how-to-play">
          <div className="generic-background-pattern">
            <IconCardBackPattern />
          </div>
          <h3 className="card__back-title">{siteText.howToPlay}</h3>
          <IconZigZagTitleUnderline />
          <OverlayScrollbarsComponent className="custom-scrollbar card__scrollbar-description">
            <p className="card__back-description" dangerouslySetInnerHTML={{ __html: cardHowToPlay1 }} />
          </OverlayScrollbarsComponent>
        </div>
      );
    }

    if (cardGameplayTip && cardGameplayTip !== '') {
      slides.push(
        <div key="back_text_3" className="card__gameplay-tip">
          <div className="corner-pattern">
            <IconCornerPattern />
          </div>
          <div className="generic-background-pattern">
            <IconCardBackPattern />
          </div>
          <div style={{ position: 'relative', width: '100%', textAlign: 'center' }}>
            <img
              className="card__back-full-character-image"
              src={characterBackFullImage}
              alt={`${sesameCharacter} character image`}
            />
          </div>
          <div className="tip">
            <h4 className="card__back-title">{siteText.gameplayTip}</h4>
            <p className="card__back-description">{cardGameplayTip}</p>
          </div>
        </div>
      );
    }

    return slides;
  };

  return (
    <div className={'card card--back'}>
      <SwiperCardBack ref={swiperRef} slides={renderSlides()} cardID={data.id} />
    </div>
  );
};

export default ActivityCardBack;
